import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Ship } from 'lucide-react';
import { useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useVessels } from '@/features/vessels/queries';
import {
  vesselDisplayName,
  type Vessel,
  type VesselCategory,
} from '@/features/vessels/types';
import { ApiError } from '@/lib/api';
import { matchesQuery } from '@/lib/list-search';
import { cn } from '@/lib/utils';

/**
 * Loading skeleton — 5 rows × 7 columns matching the actual table
 * shape, so the layout doesn't jump when data resolves.
 */
function VesselsTableSkeleton() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {Array.from({ length: 7 }).map((_, i) => (
            <TableHead key={i}>
              <Skeleton className="h-3 w-20" />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 5 }).map((_, r) => (
          <TableRow key={r}>
            {Array.from({ length: 7 }).map((_, c) => (
              <TableCell key={c}>
                <Skeleton className="h-4 w-full" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Coloured pill used in the Category column. */
function CategoryBadge({ value }: { value: VesselCategory }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
        value === 'armed'
          ? 'bg-destructive/10 text-destructive'
          : 'bg-secondary text-secondary-foreground',
      )}
    >
      {t(`vessel.category.${value}`)}
    </span>
  );
}

export function VesselsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch, isFetching } = useVessels();

  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const deferredSearch = useDeferredValue(search);

  const filtered = useMemo(() => {
    const list = data ?? [];
    return list
      .filter((v) => (showInactive ? true : !v.deletedAt))
      .filter((v) =>
        matchesQuery(v, deferredSearch, [
          (x) => x.name,
          (x) => x.imo,
          (x) => x.callSign,
          (x) => x.flag,
          (x) => x.category,
        ]),
      );
  }, [data, deferredSearch, showInactive]);

  const columns = useMemo<ColumnDef<Vessel>[]>(
    () => [
      {
        accessorKey: 'name',
        header: () => t('vessel.columns.name'),
        cell: ({ row }) => (
          <Link
            to={`/vessels/${row.original.id}`}
            onClick={(e) => e.stopPropagation()}
            className="font-medium text-primary hover:underline"
          >
            {vesselDisplayName(row.original)}
          </Link>
        ),
      },
      {
        accessorKey: 'imo',
        header: () => t('vessel.columns.imo'),
        cell: ({ getValue }) => (getValue<string | null>() ?? '—'),
      },
      {
        accessorKey: 'flag',
        header: () => t('vessel.columns.flag'),
        cell: ({ getValue }) => (getValue<string | null>() ?? '—'),
      },
      {
        accessorKey: 'category',
        header: () => t('vessel.columns.category'),
        enableSorting: false,
        cell: ({ getValue }) => (
          <CategoryBadge value={getValue<VesselCategory>()} />
        ),
      },
      {
        accessorKey: 'classSociety',
        header: () => t('vessel.columns.classSociety'),
        cell: ({ getValue }) => (getValue<string | null>() ?? '—'),
      },
      {
        accessorKey: 'homePort',
        header: () => t('vessel.columns.homePort'),
        cell: ({ getValue }) => (getValue<string | null>() ?? '—'),
      },
      {
        accessorKey: 'isActive',
        header: () => t('vessel.columns.isActive'),
        enableSorting: false,
        cell: ({ getValue }) => (
          <span
            className={cn(
              'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
              getValue<boolean>()
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {t(`vessel.status.${getValue<boolean>() ? 'active' : 'inactive'}`)}
          </span>
        ),
      },
    ],
    [t],
  );

  const errorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('vessel.list.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('vessel.list.loadError');
  })();

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t('vessel.list.title')}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('vessel.list.searchPlaceholder')}
            aria-label={t('common.search')}
            className="w-64"
            data-testid="vessels-search"
          />
          <label className="flex items-center gap-1 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              data-testid="vessels-show-inactive"
            />
            {t('common.showInactive')}
          </label>
          <Button asChild>
            <Link to="/vessels/new">
              <Plus aria-hidden />
              {t('vessel.list.newButton')}
            </Link>
          </Button>
        </div>
      </header>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('vessel.list.loadError')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMessage}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!isError && (
        <DataTable
          columns={columns}
          data={filtered}
          isLoading={isLoading}
          loadingState={<VesselsTableSkeleton />}
          emptyState={
            <EmptyState
              icon={Ship}
              title={t('vessel.list.empty')}
              description={t('vessel.list.emptyHint')}
              action={
                <Button asChild size="sm">
                  <Link to="/vessels/new">
                    <Plus aria-hidden />
                    {t('vessel.list.newButton')}
                  </Link>
                </Button>
              }
            />
          }
          onRowClick={(v) => navigate(`/vessels/${v.id}`)}
        />
      )}
    </div>
  );
}
